import { inject, Injectable } from '@angular/core';
import { Observable, of, timer, throwError } from 'rxjs';
import { switchMap, takeWhile, last } from 'rxjs/operators';
import { MapObservation, Observation, ObservationContributor, PaginatedResponse } from '../models/observation';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ObservationService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  getObservations(params?: {
    assignment?: string;
    creator?: string;
    status?: string;
    living_thing?: string;
    event?: string;
    equipment?: string;
    recording_session?: string;
    page?: number;
    page_size?: number;
    ordering?: string;
  }): Observable<PaginatedResponse<Observation>> {
    let httpParams = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== 'undefined') {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }
    return this.http.get<PaginatedResponse<Observation>>(this.apiUrl + '/monitoring/observation/', { params: httpParams });
  }

  getPublicObservations(params?: {
    page?: number;
    page_size?: number;
  }): Observable<PaginatedResponse<Observation>> {
    let httpParams = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }
    return this.http.get<PaginatedResponse<Observation>>(this.apiUrl + '/monitoring/observation/public/', { params: httpParams });
  }

  getMapObservations(bbox?: string): Observable<MapObservation[]> {
    let httpParams = new HttpParams();
    if (bbox) {
      httpParams = httpParams.set("bbox", bbox);
    }
    return this.http.get<MapObservation[]>(this.apiUrl + "/monitoring/observation/map/", { params: httpParams });
  }

  getPublicMapObservations(bbox?: string): Observable<MapObservation[]> {
    let httpParams = new HttpParams();
    if (bbox) {
      httpParams = httpParams.set("bbox", bbox);
    }
    return this.http.get<MapObservation[]>(this.apiUrl + "/monitoring/observation/map-public/", { params: httpParams });
  }

  getObservation(id: string): Observable<Observation | undefined> {
    return this.http.get<Observation>(this.apiUrl + '/monitoring/observation/' + id);
  }

  getObservationMedia(mediaId: string): Observable<any> {
    return this.http.get<any>(this.apiUrl + '/monitoring/media/' + mediaId);
  }

  addObservation(observation: Observation): Observable<Observation> {
    return this.http.post<Observation>(this.apiUrl + '/monitoring/observation/', observation);
  }

  updateObservation(id: string, observation: Observation): Observable<Observation> {
    return this.http.put<Observation>(this.apiUrl + '/monitoring/observation/' + id + '/', observation);
  }

  updateGuess(id: number, data: { status: number; feedback_text?: string }): Observable<any> {
    return this.http.patch<any>(this.apiUrl + '/monitoring/guess/' + id + '/', data);
  }

  createGuess(data: { observation: number; living_thing: number; status?: number }): Observable<any> {
    return this.http.post<any>(this.apiUrl + '/monitoring/guess/', data);
  }

  deleteObservation(id: string): Observable<void> {
    return this.http.delete<void>(this.apiUrl + '/monitoring/observation/' + id + '/');
  }

  getProfile(): Observable<any> {
    return this.http.get<any>(this.apiUrl + '/profile/');
  }

  getEnums(): Observable<any> {
    return this.http.get<any>(this.apiUrl + '/enums/');
  }

  getLivingThings(params?: { q?: string; starts_with?: string }): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== 'undefined') {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }
    return this.http.get<any>(this.apiUrl + '/atlas/livingthing/', { params: httpParams });
  }

  getExperts(): Observable<any[]> {
    return this.http.get<any[]>(this.apiUrl + '/experts/');
  }

  assignObservation(id: string, userId: string | number | null): Observable<Observation> {
    return this.http.patch<Observation>(this.apiUrl + '/monitoring/observation/' + id + '/', { assigned_to: userId });
  }

  analyzeWithBirdNet(mediaId: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/monitoring/media/${mediaId}/birdnet/`, {}).pipe(
      switchMap((response) => {
        if (response.status !== 'processing' || !response.task_id) {
          return of(response);
        }
        return this.pollBirdNetStatus(
          `${this.apiUrl}/monitoring/media/${mediaId}/birdnet-status/`,
          response.task_id
        );
      })
    );
  }

  // --- Contributor management ---

  getContributors(observationId: string | number): Observable<ObservationContributor[]> {
    return this.http.get<ObservationContributor[]>(
      `${this.apiUrl}/monitoring/contributors/for_observation/?observation=${observationId}`
    );
  }

  addContributorByEmail(observationId: number, email: string): Observable<ObservationContributor> {
    return this.http.post<ObservationContributor>(`${this.apiUrl}/monitoring/contributors/`, {
      observation: observationId,
      email,
    });
  }

  removeContributor(contributorId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/monitoring/contributors/${contributorId}/`);
  }

  generateInviteToken(observationId: number): Observable<{ observation: number; token: string; invite_url: string }> {
    return this.http.post<any>(`${this.apiUrl}/monitoring/contributors/generate_invite/`, {
      observation: observationId,
    });
  }

  acceptInvite(token: string): Observable<ObservationContributor> {
    return this.http.post<ObservationContributor>(`${this.apiUrl}/monitoring/contributors/accept_invite/`, {
      token,
    });
  }

  analyzeAudioFile(file: File, lat?: number, lon?: number, date?: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (lat) formData.append('lat', lat.toString());
    if (lon) formData.append('lon', lon.toString());
    if (date) formData.append('date', date);

    return this.http.post<any>(`${this.apiUrl}/monitoring/media/analyze_file/`, formData).pipe(
      switchMap((response) => {
        if (response.status !== 'processing' || !response.task_id) {
          return of(response);
        }
        return this.pollBirdNetStatus(
          `${this.apiUrl}/monitoring/media/birdnet-file-status/`,
          response.task_id
        );
      })
    );
  }

  private pollBirdNetStatus(url: string, taskId: string): Observable<any> {
    const POLL_INTERVAL = 2000;
    const MAX_POLLS = 90; // 3 minutes max

    return timer(0, POLL_INTERVAL).pipe(
      switchMap((attempt) => {
        if (attempt >= MAX_POLLS) {
          return throwError(() => new Error('BirdNet analysis timed out. Please try again.'));
        }
        return this.http.get<any>(url, { params: { task_id: taskId } });
      }),
      takeWhile((response) => response.status === 'processing', true),
      last(),
      switchMap((response) => {
        if (response.status === 'error') {
          return throwError(() => new Error(response.detail || 'BirdNet analysis failed.'));
        }
        return of(response);
      })
    );
  }
}
